import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { useDetailTenantId } from "@shared/navigation/tabs/useDetailTenantId";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { ServiceDetailsPageSkeleton } from "kontrolEdge/components/Shared/ServiceDetailsPageSkeleton";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import CarrierBridgeBandwidthChart from "./CarrierBridgeBandwidthChart";
import { useCarrierBridgeMeta } from "./useCarrierBridgeMeta";
import { EditableTagList } from "kontrolEdge/components/Shared/NameTagEdit/EditableTagList";
import StatusTable from "kontrolEdge/components/Port/PortDetails/StatusTable";
import { resolveServiceStatusTag } from "kontrolEdge/helpers/serviceStatusTag";
import { CARRIER_BRIDGE_LABEL } from "kontrolEdge/helpers/carrierBridgeName";
import {
  fetchCarrierBridgeDetail,
  fetchCarrierBridgePerformance,
} from "../requests/request";
import {
  CarrierBridgeDetailResponse,
  shapeCarrierBridgeDetail,
  toActivityEntries,
} from "./CarrierBridgeDetailsData";

const CARRIER_BRIDGE_LIST_PATH = "/kontrol/carrier-bridge";

const NO_TAGS: string[] = [];

/** Label left, value right, bottom border: the row every detail card uses. */
const DetailRow: React.FC<{
  label: string;
  children: React.ReactNode;
  isLast?: boolean;
}> = ({ label, children, isLast = false }) => (
  <div
    className={`flex justify-between items-center gap-4 w-full py-1 ${
      isLast ? "" : "border-b"
    }`}
  >
    <h3 className="text-sm text-semantic-text-primary">{label}</h3>
    <div className="flex items-center justify-end gap-2 min-w-0">
      {typeof children === "string" ? (
        <p className="text-sm text-semantic-text-primary">{children}</p>
      ) : (
        children
      )}
    </div>
  </div>
);

/**
 * Carrier Bridge detail for the customer (CTX-726), per the design José
 * approved on 28-sep: connection and subscription cards side by side, the
 * live Service Status inside Subscription Details, the bandwidth of the
 * customer side below, and the provisioning history last, in the same
 * "Activity" card Port uses.
 *
 * Name and tags are the only things the customer edits; the service itself
 * changes from the Admin Center.
 */
const CarrierBridgeDetails: React.FC = () => {
  const api = useAxiosAuth0();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const carrierBridgeId = Number(searchParams.get("carrierBridgeId"));

  const reduxTenantId = useAppSelector((state) => state.selection.tenantId);
  // `?tenantId=` when the URL carries it (a partner opening a customer's row),
  // else the selection: the page rebuilds from its URL alone (CTX-861).
  const effectiveTenantId = useDetailTenantId();

  const [detail, setDetail] = useState<CarrierBridgeDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Switching tenant while here leaves a service that is no longer in scope.
  const initialTenantIdRef = useRef(reduxTenantId);
  useEffect(() => {
    if (reduxTenantId !== initialTenantIdRef.current) {
      navigate(CARRIER_BRIDGE_LIST_PATH);
    }
  }, [reduxTenantId, navigate]);

  useEffect(() => {
    if (!Number.isInteger(carrierBridgeId) || carrierBridgeId <= 0) {
      setError(`Missing ${CARRIER_BRIDGE_LABEL} id`);
      return;
    }
    if (effectiveTenantId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchCarrierBridgeDetail(
          api,
          carrierBridgeId,
          effectiveTenantId
        );
        if (cancelled) return;
        if (data.success && data.data) {
          setDetail(data.data);
        } else {
          setError(data.message || "Could not load the details");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading Carrier Bridge details", err);
        setError(
          err instanceof AxiosError
            ? err.response?.data?.message ?? err.message
            : "Network error"
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, carrierBridgeId, effectiveTenantId]);

  const view = useMemo(
    () => (detail ? shapeCarrierBridgeDetail(detail) : null),
    [detail]
  );
  const activity = useMemo(
    () => toActivityEntries(detail?.statusHistory),
    [detail?.statusHistory]
  );

  // The row's own tenant, so a partner's edit lands on the customer's service.
  const resourceTenantId = detail?.tenantId ?? effectiveTenantId ?? 0;

  const meta = useCarrierBridgeMeta({
    api,
    carrierBridgeId: detail?.id ?? null,
    tenantId: resourceTenantId,
    initialName: view?.name ?? "",
    // A stable empty list: a fresh [] per render would re-seed forever.
    initialTags: view?.tags ?? NO_TAGS,
  });

  const fetchUtilization = useCallback(
    async (timeRange: string, interval: string) => {
      if (effectiveTenantId == null) return null;
      const { data } = await fetchCarrierBridgePerformance(
        api,
        carrierBridgeId,
        effectiveTenantId,
        timeRange,
        interval
      );
      const utilization = data.data?.bandwidthUtilization;
      return data.success && utilization ? utilization : null;
    },
    [api, carrierBridgeId, effectiveTenantId]
  );

  if (error) return <ErrorFetchingItems text={error} />;
  if (!detail || !view) return <ServiceDetailsPageSkeleton />;

  const serviceStatus = resolveServiceStatusTag(view.serviceStatus);
  const portDetailPath =
    view.portId != null
      ? buildDetailPath("port", { portId: view.portId }, { subid: view.portSubid })
      : null;

  return (
    <div className="px-4 overflow-y-auto h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent={`Connection Details (${CARRIER_BRIDGE_LABEL})`}
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="Service ID">{view.serviceId}</DetailRow>
                <DetailRow label="Name">
                  <GenericTag
                    label={meta.nameValue || "—"}
                    variant="neutral"
                    size="small"
                    minWidth="100px"
                    className="text-center"
                    editable
                    value={meta.nameValue}
                    onSave={meta.saveName}
                    onCancel={meta.cancelName}
                    isSaving={meta.savingField === "name"}
                    disabled={meta.savingField !== null}
                  />
                </DetailRow>
                <DetailRow label="Tags">
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    {/* Free-text tags by default; the Tags Manager picker once
                        the backend knows the type (CTX-721). */}
                    <EditableTagList
                      tags={meta.tags}
                      onUpdate={meta.updateTags}
                      disabled={meta.savingField !== null}
                      isLoading={meta.savingField === "tag"}
                      resourceType="ke-carrier-bridge"
                      resourceId={detail.id}
                      tenantId={resourceTenantId}
                    />
                  </div>
                </DetailRow>
                <DetailRow label="Carrier">{view.carrierName}</DetailRow>
                <DetailRow label="Customer port">
                  {view.portId != null ? (
                    <button
                      type="button"
                      {...viewHref(portDetailPath)}
                      onClick={() => portDetailPath && navigate(portDetailPath)}
                      className="text-sm font-medium underline text-semantic-text-brand"
                    >
                      {view.portSubid}
                    </button>
                  ) : (
                    view.portSubid
                  )}
                </DetailRow>
                <DetailRow label="Location">{view.location}</DetailRow>
                <DetailRow label="VLAN">{view.vlan}</DetailRow>
                <DetailRow label="Capacity" isLast={!view.circuitId}>
                  {view.capacity}
                </DetailRow>
                {view.circuitId && (
                  <DetailRow label="Circuit ID" isLast>
                    {view.circuitId}
                  </DetailRow>
                )}
              </div>
            }
          />
        </div>

        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Subscription Details"
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="Term">{view.term}</DetailRow>
                <DetailRow label="Auto Renew">{view.autoRenew}</DetailRow>
                <DetailRow label="Renew Date">{view.renewDate}</DetailRow>
                <DetailRow label="Subscription Status">
                  {view.subscriptionStatus}
                </DetailRow>
                <DetailRow label="Service Status" isLast={!view.serviceStatusMessage}>
                  <ServiceStatusValue
                    label={serviceStatus.label}
                    variant={serviceStatus.variant}
                    tooltip={view.serviceStatusMessage ?? undefined}
                  />
                </DetailRow>
                {view.serviceStatusMessage && (
                  <p className="text-sm text-semantic-text-secondary">
                    {view.serviceStatusMessage}
                  </p>
                )}
              </div>
            }
          />
        </div>
      </div>

      <CarrierBridgeBandwidthChart
        title="Bandwidth Utilization"
        fetchUtilization={fetchUtilization}
      />

      {activity.length > 0 && (
        <StatusTable
          data={activity}
          headerContent="Activity"
        />
      )}
    </div>
  );
};

export default CarrierBridgeDetails;
